import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, IdCard, KeyRound, LogOut } from 'lucide-react';
import { Avatar, cn } from '@hrms/ui';
import { needsSalaryPin } from '@hrms/shared';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { LazyCreateCompanyDialog } from '../company-group/LazyCreateCompanyDialog.js';
import { useCompanyGroup } from '../company-group/useCompanyGroup.js';

export interface ProfileMenuProps {
  onSignOut: () => void;
  signingOut: boolean;
}

/**
 * The topbar avatar and the small menu it opens: who is signed in, a link to their profile, "Create
 * another company" for someone allowed to (docs/adr/0034-company-groups.md), and log out. Closes on
 * outside click, Escape, or choosing an item.
 */
export function ProfileMenu({ onSignOut, signingOut }: ProfileMenuProps) {
  const { me, actor } = useAuthenticatedSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const { canCreateCompany } = useCompanyGroup();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-full transition-opacity hover:opacity-80 sm:min-h-0 sm:min-w-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <Avatar
          name={me.person.displayName}
          imageUrl={me.person.photoUrl}
          colorSeed={me.person.id}
          size="md"
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-full z-40 mt-2 w-60 rounded-xl border border-border bg-surface p-1.5 shadow-lg"
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-semibold text-fg">{me.person.displayName}</p>
            <p className="truncate text-xs text-fg-muted">{me.person.email}</p>
          </div>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/my-details');
            }}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium text-fg transition-colors hover:bg-bg-subtle"
          >
            <IdCard className="size-4 text-fg-muted" />
            My profile
          </button>
          {/* Only for people who are ever asked for one (ADR 0041). */}
          {needsSalaryPin(actor) && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/my-details?tab=more');
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium text-fg transition-colors hover:bg-bg-subtle"
            >
              <KeyRound className="size-4 text-fg-muted" />
              Salary PIN
            </button>
          )}
          {canCreateCompany && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setCreating(true);
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium text-fg transition-colors hover:bg-bg-subtle"
            >
              <Building2 className="size-4 text-fg-muted" />
              Create another company
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            disabled={signingOut}
            className={cn(
              'mt-1 flex w-full items-center gap-2.5 rounded-md bg-danger-subtle px-2.5 py-2 text-left text-sm font-medium text-danger transition-colors hover:bg-danger/15',
              signingOut && 'opacity-60',
            )}
          >
            <LogOut className="size-4" />
            {signingOut ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      )}
      <LazyCreateCompanyDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
